import type { ChatUiMobilityOffer } from '@voll-intelligence/genui-contract';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { fmtMoney } from '../../../shared/ui/flight_format.js';
import { useGenUiI18n, type GenUiLocale, type GenUiTranslate } from '../../../shared/ui/i18n.js';
import { RIDE_ART } from '../../shared/ride_art/ride_art.js';
import { supplierDisplayName } from '../../shared/supplier_name.js';

/**
 * «Corrida · linha de opção» (#6321, Figma 6903:346192; estreita 6875:45609): uma oferta
 * dentro da casca do bloco, sem sombra nem borda própria — o fio entre as linhas e o hover
 * em `--sand-25` são da lista. Na densidade dos cards 1-Click de hotel e locação (#6323,
 * rodada 3): 62 px, 8 em cima e embaixo, 16 entre as colunas, tudo centrado na linha. À
 * esquerda o carro da categoria (`RIDE_ART`); no meio o título (o fornecedor em nome próprio)
 * e o veículo, e os fatos (distância, franquia) sem glifo; à direita o total que a pessoa paga
 * e o «Escolher». A política por distância não marca a linha: vale para a corrida inteira e
 * avisa no topo das opções (#6699).
 *
 * Na coluna estreita a ilustração encolhe para 63×36, o título quebra em até duas linhas, o
 * veículo passa para a linha dos fatos e o preço vira a última linha do bloco do meio; o
 * «Escolher» segue à direita, no degrau de toque (44 px).
 *
 * Sem `onSelect` a linha é só consulta (a pilha pós-escolha); a escolhida leva o ✓
 * «Escolhida» no lugar do botão.
 */
export function MobilityRow({
  offer,
  position,
  selected = false,
  roundTrip = false,
  estimateNote = false,
  onSelect,
}: {
  readonly offer: ChatUiMobilityOffer;
  readonly position: number;
  /** Oferta escolhida (pilha pós-escolha) — ganha o ✓ «Escolhida». */
  readonly selected?: boolean;
  /** Ida e volta: o preço da corrida é o das 2 corridas, a distância é por trecho. */
  readonly roundTrip?: boolean;
  /** A nota do valor previsto embaixo do preço (a corrida escolhida, na pilha). */
  readonly estimateNote?: boolean;
  readonly onSelect?: (offer: ChatUiMobilityOffer, position: number) => void;
}) {
  const { t } = useGenUiI18n();
  const art = RIDE_ART[offer.section];
  const trailing = onSelect ? (
    <Button
      variant="outline"
      className={CHOOSE}
      aria-label={t('mobility.chooseAria', {
        title: supplierDisplayName(offer.title),
        price: offer.price ? fmtMoney(offer.price) : t('mobility.priceOnRequest'),
        label: priceLabel(offer, roundTrip, t) ?? '',
      })}
      onClick={() => {
        onSelect(offer, position);
      }}
    >
      {t('mobility.choose')}
    </Button>
  ) : selected ? (
    <span className="inline-flex items-center gap-1 text-caption font-medium text-foreground">
      <CheckCircleIcon size={16} aria-hidden />
      {t('mobility.chosen')}
    </span>
  ) : null;
  return (
    <li
      className={`grid items-center gap-x-4 gap-y-0.5 py-2 transition-colors hover:bg-(--sand-25) @max-[theme(--breakpoint-sm)]:gap-x-3 ${
        trailing
          ? `grid-cols-[auto_minmax(0,1fr)_auto_auto] @max-[theme(--breakpoint-sm)]:grid-cols-[auto_minmax(0,1fr)_auto]`
          : `grid-cols-[auto_minmax(0,1fr)_auto] @max-[theme(--breakpoint-sm)]:grid-cols-[auto_minmax(0,1fr)]`
      }`}
    >
      {/* Decorativa: quem nomeia a oferta é o título ao lado (e a seção acima). */}
      <img
        src={art.src}
        srcSet={art.srcSet}
        width={80}
        height={46}
        alt=""
        draggable={false}
        className={`h-[46px] w-20 object-contain @max-[theme(--breakpoint-sm)]:row-span-2 @max-[theme(--breakpoint-sm)]:h-9 @max-[theme(--breakpoint-sm)]:w-[63px]`}
      />
      <MobilityFacts offer={offer} roundTrip={roundTrip} />
      <MobilityPrice offer={offer} roundTrip={roundTrip} estimateNote={estimateNote} />
      {trailing ? (
        <span
          className={`@max-[theme(--breakpoint-sm)]:col-start-3 @max-[theme(--breakpoint-sm)]:row-span-2 @max-[theme(--breakpoint-sm)]:row-start-1`}
        >
          {trailing}
        </span>
      ) : null}
    </li>
  );
}

/** O «Escolher»: `size=default` (32 px) no largo e o degrau de toque `xl` (44 px) na coluna
 *  estreita — o tamanho segue a largura da lista (`@container`), como o resto da linha. */
const CHOOSE =
  '@max-[theme(--breakpoint-sm)]:h-11 @max-[theme(--breakpoint-sm)]:gap-2 @max-[theme(--breakpoint-sm)]:px-4 @max-[theme(--breakpoint-sm)]:text-[0.9375rem]';

/** Um fato da oferta (veículo, distância, franquia): 13/18 em `muted-foreground`, sem glifo.
 *  Inteiro numa linha no largo; no estreito quebra, senão a franquia atravessa a coluna e
 *  passa por baixo do «Escolher» (320 de tela). */
const FACT =
  'text-detail whitespace-nowrap text-muted-foreground @max-[theme(--breakpoint-sm)]:whitespace-normal';

/**
 * O bloco do meio, numa lista só que quebra: no largo, o título e o veículo na 1ª linha e os
 * fatos na 2ª (a quebra é o `span` de 2 px); no estreito, o título sozinho (até duas linhas) e
 * o veículo junto dos fatos — a ordem muda pelo `order`, o DOM e a leitura seguem título →
 * veículo → fatos.
 */
function MobilityFacts({
  offer,
  roundTrip,
}: {
  readonly offer: ChatUiMobilityOffer;
  readonly roundTrip: boolean;
}) {
  const { t, locale } = useGenUiI18n();
  const facts = factsOf(offer, roundTrip, t, locale);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 @max-[theme(--breakpoint-sm)]:gap-y-0.5">
      <span
        className={`text-ui text-foreground @max-[theme(--breakpoint-sm)]:line-clamp-2 @max-[theme(--breakpoint-sm)]:basis-full`}
      >
        {supplierDisplayName(offer.title)}
      </span>
      {offer.vehicle ? (
        <span className={`${FACT} @max-[theme(--breakpoint-sm)]:order-2`}>{offer.vehicle}</span>
      ) : null}
      {facts.length > 0 ? (
        <span aria-hidden className="h-0.5 basis-full @max-[theme(--breakpoint-sm)]:hidden" />
      ) : null}
      {facts.map((fact) => (
        <span key={fact.key} className={`${FACT} @max-[theme(--breakpoint-sm)]:order-2`}>
          {fact.label}
        </span>
      ))}
    </div>
  );
}

/** O bloco do preço: o valor e o rótulo embaixo; na coluna estreita ele vira a última linha do
 *  bloco do meio, valor e rótulo na mesma linha — o rótulo desce quando não cabe e, no estreito,
 *  quebra também por dentro; o total nunca quebra. */
const PRICE = `flex flex-col items-end text-right @max-[theme(--breakpoint-sm)]:col-start-2 @max-[theme(--breakpoint-sm)]:row-start-2 @max-[theme(--breakpoint-sm)]:flex-row @max-[theme(--breakpoint-sm)]:flex-wrap @max-[theme(--breakpoint-sm)]:items-baseline @max-[theme(--breakpoint-sm)]:gap-x-1.5 @max-[theme(--breakpoint-sm)]:text-left`;

/**
 * O preço da linha: o total que a pessoa paga, e no rótulo o que ele é — as duas seções
 * comparam no mesmo eixo. Corrida: o valor previsto («ida e volta · valor previsto» com volta,
 * o total das 2 corridas), e com `estimateNote` a nota de que o valor final depende do trajeto
 * e do trânsito. Carro com motorista:
 * `price` já é o total das diárias (o agents multiplica a diária pelas diárias), e o rótulo
 * diz quantas e de quanto («2 diárias de R$ 420,00»); sem `days`, é uma diária só. Sem preço,
 * "Sob consulta".
 */
function MobilityPrice({
  offer,
  roundTrip,
  estimateNote,
}: {
  readonly offer: ChatUiMobilityOffer;
  readonly roundTrip: boolean;
  readonly estimateNote: boolean;
}) {
  const { t } = useGenUiI18n();
  const label = priceLabel(offer, roundTrip, t);
  return (
    <div className={PRICE}>
      <span className="text-ui whitespace-nowrap text-foreground tabular-nums">
        {offer.price ? fmtMoney(offer.price) : t('mobility.priceOnRequest')}
      </span>
      {label ? (
        <span
          className={`text-caption whitespace-nowrap text-muted-foreground @max-[theme(--breakpoint-sm)]:whitespace-normal`}
        >
          {label}
        </span>
      ) : null}
      {estimateNote && offer.section === 'cooperative' ? (
        <span className="basis-full text-caption text-muted-foreground">
          {t('mobility.estimateNote')}
        </span>
      ) : null}
    </div>
  );
}

function priceLabel(
  offer: ChatUiMobilityOffer,
  roundTrip: boolean,
  t: GenUiTranslate,
): string | undefined {
  if (offer.section === 'cooperative') {
    return t(roundTrip ? 'mobility.roundTripEstimate' : 'mobility.estimate');
  }
  const { price, days } = offer;
  if (price) {
    if (!days) return t('mobility.perDailySuffix');
    return t(days === 1 ? 'mobility.daysOfDaily_one' : 'mobility.daysOfDaily_other', {
      count: days,
      daily: fmtMoney({ ...price, amount: price.amount / days }),
    });
  }
  if (days) return t(days === 1 ? 'mobility.day_one' : 'mobility.day_other', { count: days });
  return offer.unitLabel;
}

/** Os fatos sob o título: a distância (corrida) e a franquia da diária (carro com
 *  motorista). Somem quando o provider não os manda — a linha degrada para título + preço. */
function factsOf(
  offer: ChatUiMobilityOffer,
  roundTrip: boolean,
  t: GenUiTranslate,
  locale: GenUiLocale,
): { key: string; label: string }[] {
  const facts: { key: string; label: string }[] = [];
  if (offer.distanceKm != null) {
    const km = formatKm(offer.distanceKm, locale);
    const key = roundTrip && offer.section === 'cooperative' ? 'distanceKmPerLeg' : 'distanceKm';
    facts.push({ key: 'km', label: t(`mobility.${key}`, { km }) });
  }
  const franchise = franchiseLabel(offer, t, locale);
  if (franchise) facts.push({ key: 'hours', label: franchise });
  return facts;
}

/** A franquia da diária: "Diária 10h com 100 km" ou "… com km livre" quando a
 *  tarifa a traz; sem ela (cartão antigo), as horas por dia, como antes. */
function franchiseLabel(
  offer: ChatUiMobilityOffer,
  t: GenUiTranslate,
  locale: GenUiLocale,
): string | null {
  const { hours, franchiseKm, freeKm } = offer;
  if (hours == null) return null;
  if (freeKm) return t('mobility.franchiseFreeKm', { hours });
  if (franchiseKm) return t('mobility.franchise', { hours, km: formatKm(franchiseKm, locale) });
  return t('mobility.hoursPerDay', { hours });
}

/** "9,4" (pt-BR) / "9.4" (en) / "12" — distância no idioma do card, sem casas quando inteira,
 *  uma casa quando fracionária. */
export function formatKm(km: number, locale: GenUiLocale): string {
  return km.toLocaleString(locale, { maximumFractionDigits: 1 });
}
